import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../../redux/Store';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  TelcoBillSettingsType,
  TelcoBillSettingsResponse,
  CreateTelcoBillSettingsDto,
  UpdateTelcoBillSettingsDto,
  PackagesResponse,
  PackageProductsResponse,
  BoboPackagesResponse,
  BoboPackageProductsResponse,
  PackageProductsQueryParams,
  BoboPackageProductsQueryParams,
  CreateRegionSettingsDto,
  UpdateRegionSettingsDto,
  RegionSettings,
  RegionSettingsResponse,
  StateRegionResponse,
} from '../../types/telcobill-package-settings/telcobillPackageSettings.types';
import { rejectWithApiError } from '../../utils/api';

export const getTcpackages = createAsyncThunk<PackagesResponse, void, { state: RootState }>(
  'telcobillPackageSettings/getTcpackages',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<PackagesResponse>(`${process.env.REACT_APP_API_URL}/revio/packages`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching TC packages!' });
    }
  }
);

export const getBoboPackages = createAsyncThunk<BoboPackagesResponse, number | void | null, { state: RootState }>(
  'telcobillPackageSettings/getBoboPackages',
  async (revioAgentId, thunkAPI) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/revio/packages-bobo`;

      if (revioAgentId && typeof revioAgentId === 'number') {
        url += `?bill_profile_id=${revioAgentId}`;
      }

      const response = await axios.get<BoboPackagesResponse>(url);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching Bobo packages!' });
    }
  }
);

export const getPackageProducts = createAsyncThunk<
  PackageProductsResponse,
  PackageProductsQueryParams,
  { state: RootState }
>('telcobillPackageSettings/getPackageProducts', async (params, thunkAPI) => {
  try {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryString.append(key, value.toString());
      }
    });

    const response = await axios.get<PackageProductsResponse>(
      `${process.env.REACT_APP_API_URL}/revio/package_products?${queryString}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching package products!' });
  }
});

export const getBoboPackageProducts = createAsyncThunk<
  BoboPackageProductsResponse,
  BoboPackageProductsQueryParams,
  { state: RootState }
>('telcobillPackageSettings/getBoboPackageProducts', async (params, thunkAPI) => {
  try {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryString.append(key, value.toString());
      }
    });

    const response = await axios.get<BoboPackageProductsResponse>(
      `${process.env.REACT_APP_API_URL}/revio/bobo_package_products?${queryString}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching Bobo package products!' });
  }
});

export const getTelcoBillSettings = createAsyncThunk<TelcoBillSettingsResponse, number, { state: RootState }>(
  'telcobillPackageSettings/getTelcoBillSettings',
  async (organizationId, thunkAPI) => {
    try {
      const response = await axios.get<TelcoBillSettingsResponse>(
        `${process.env.REACT_APP_API_URL}/telcobill/billing-settings/${organizationId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching TelcoBill settings!' });
    }
  }
);

export const getTelcoBillSettingsPricing = createAsyncThunk<
  TelcoBillSettingsType,
  { organizationId: number; creditType: number },
  { state: RootState }
>('telcobillPackageSettings/getTelcoBillSettingsPricing', async ({ organizationId, creditType }, thunkAPI) => {
  try {
    const response = await axios.get<TelcoBillSettingsResponse>(
      `${process.env.REACT_APP_API_URL}/telcobill/billing-settings/${organizationId}?creditType=${creditType}`
    );

    const specificItem = response.data.items?.find(item => item.creditType === creditType);
    if (!specificItem) {
      throw new Error('Setting not found');
    }

    return specificItem;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching TelcoBill settings pricing!' });
  }
});

export const createTelcoBillSettings = createAsyncThunk<
  TelcoBillSettingsType,
  CreateTelcoBillSettingsDto,
  { state: RootState }
>('telcobillPackageSettings/createTelcoBillSettings', async (data, thunkAPI) => {
  try {
    const response = await axios.post<TelcoBillSettingsType>(
      `${process.env.REACT_APP_API_URL}/telcobill/billing-settings`,
      data
    );

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error creating TelcoBill settings!' });
  }
});

export const updateTelcoBillSettings = createAsyncThunk<
  TelcoBillSettingsType,
  { id: number; data: UpdateTelcoBillSettingsDto },
  { state: RootState }
>('telcobillPackageSettings/updateTelcoBillSettings', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.put<TelcoBillSettingsType>(
      `${process.env.REACT_APP_API_URL}/telcobill/billing-settings/${id}`,
      data
    );

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating TelcoBill settings!' });
  }
});

// Region Settings
export const createRegionSettings = createAsyncThunk<
  RegionSettings,
  { organizationId: number; data: CreateRegionSettingsDto },
  { state: RootState }
>('telcobillPackageSettings/createRegionSettings', async ({ organizationId, data }, thunkAPI) => {
  try {
    const payloadWithOrganization = {
      ...data,
      organization: {
        id: organizationId,
      },
    };

    const response = await axios.post<RegionSettings>(
      `${process.env.REACT_APP_API_URL}/telco-bill-region-settings`,
      payloadWithOrganization
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error creating region settings!' });
  }
});

export const getRegionSettingsByOrganization = createAsyncThunk<RegionSettingsResponse, number, { state: RootState }>(
  'telcobillPackageSettings/getRegionSettingsByOrganization',
  async (organizationId, thunkAPI) => {
    try {
      const response = await axios.get<RegionSettings[]>(
        `${process.env.REACT_APP_API_URL}/telco-bill-region-settings/?org_id=${organizationId}`
      );

      const formattedResponse = {
        items: response.data,
        total: response.data.length,
        page: 1,
        limit: response.data.length,
      };
      return formattedResponse;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching region settings!' });
    }
  }
);

export const getRegionByStateCode = createAsyncThunk<
  StateRegionResponse,
  { organizationId: number; stateCode: string },
  { state: RootState }
>('telcobillPackageSettings/getRegionByStateCode', async ({ organizationId, stateCode }, thunkAPI) => {
  try {
    const response = await axios.get<StateRegionResponse>(
      `${process.env.REACT_APP_API_URL}/telco-bill-region-settings/${organizationId}/state/${stateCode}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching region by state code!' });
  }
});

export const updateRegionSettings = createAsyncThunk<
  RegionSettings,
  { id: number; organizationId: number; data: UpdateRegionSettingsDto },
  { state: RootState }
>('telcobillPackageSettings/updateRegionSettings', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<RegionSettings>(
      `${process.env.REACT_APP_API_URL}/telco-bill-region-settings/${id}`,
      data
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating region settings!' });
  }
});

export const deleteRegionSettings = createAsyncThunk<{ message: string }, number, { state: RootState }>(
  'telcobillPackageSettings/deleteRegionSettings',
  async (id, thunkAPI) => {
    try {
      const response = await axios.delete<{ message: string }>(
        `${process.env.REACT_APP_API_URL}/telco-bill-region-settings/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error deleting region settings!' });
    }
  }
);
